import React, { FormEvent, FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import CreatableAsyncSelect from '../components/select/CreatableAsyncSelect'
import { useTagsRequest } from '../services/apis/tags/useTagsRequest'
import debounce from 'lodash.debounce'
import Tag from '../components/Tag'
import Row from '../components/Row'
import SvgFragment from './SvgFragment'
import Text from '../components/Text'
import { styles } from '../styles/themes/style'
import Column from '../components/Column'
import FormControl from '../components/FormControl'
import ColorSelectDropdownFragment from './ColorSelectDropdownFragment'
import { useAppHandlers } from '../services/actions/app/useAppHandlers'
import PrivateCheckBoxControl from './PrivateCheckBoxControl'
import { ITag } from '../interfaces/models/connection'
import { useAuthStore } from "../stores/auth/useAuthStore"
import AppTag from './AppTag'

interface IApplyRemoveTagsSelectProps {
    prefilledValue?: ITag[],
    onChangeHandler?: (e: any) => void
}

export interface IEditableTag {
    _id?: string,
    color?: string,
    title?: string,
    value?: string,
    label?: string,
    private?: boolean,
    new?: boolean,
    isForbidden?: boolean,
    forKanban?: boolean,
    kanban?: any
}

const ApplyRemoveTagsSelect: FunctionComponent<IApplyRemoveTagsSelectProps> = ({ prefilledValue, onChangeHandler }) => {

    const { getTagsPaginate } = useTagsRequest();
    const { setCustomColorModalHandler } = useAppHandlers();
    const { store: { workspace} } = useAuthStore()
    const [ value, setValue ] = useState<IEditableTag[]>([])
    const [ selected, setSelected ] = useState<IEditableTag | undefined>(undefined)

    useEffect(() => {
        setValue(prefilledValue?.map(tag => ({
            ...tag,
            value: tag.title,
            label: tag.title
        })) ?? [])
    }, [])

    useEffect(()=>{
        onChangeHandler &&  onChangeHandler(value)
    }, [value])

    const onRemove = useCallback((option: IEditableTag) => {
        if(!option.isForbidden){
            const valueNew = value.filter(item => item.title !== option.title); 
            setValue(valueNew)
            const lastTag = valueNew && valueNew.length ? valueNew[valueNew.length - 1] : undefined; 
            setSelected(lastTag?.new ? lastTag : undefined)
        }
    }, [value])

    const onChange = useCallback((values: IEditableTag[]) => {
        const valueNew = [...values]
        setValue(valueNew)
        const lastTag = valueNew && valueNew.length ? valueNew[valueNew.length - 1] : undefined; 
        setSelected(lastTag?.new ? lastTag : undefined)
    }, [value])

    const onCreate = useCallback((newTitle: string) => {
        const newTag = {
            color: styles.colors.primary600,
            title: newTitle,
            private: true,
            new: true,
            label: newTitle,
            value: newTitle
        }
        setValue([...value, newTag])
        setSelected(newTag)
    }, [value])

    const updateSelected = useCallback((newSelected: IEditableTag) => {
        let newValue = value.map((tag: IEditableTag) => newSelected.title === tag.title ? { ...newSelected } : tag)
        setSelected({ ...newSelected })
        setValue([ ...newValue ])
    }, [selected, value])

    const loadOptionsHandler = debounce(async (inputValue: string, callback: any) => {
            try{
                let tagsData = inputValue?.trim()?.length ? await getTagsPaginate({page: 1, pageSize: 10, searchByName: inputValue}) : await getTagsPaginate({page: 1, pageSize: 100, searchByName: ""});
                //todo: filter for everything but forbidden
                

                tagsData = tagsData ? tagsData.tags.filter((tag: ITag) => !tag.isForbidden)?.sort((a: ITag, b: ITag) => a?.title?.localeCompare(b?.title)) : []

                callback(tagsData?.map((tag: ITag) => ({
                    ...tag,
                    label: tag.title,
                    value: tag.title
                })))
            }
            catch(error){
                callback([])
            }   
    }, 500)

    return <Column gap='10px'>
                <CreatableAsyncSelect 
                isSearchable
                isMulti
                placeholder = {'Type tag name...'}
                maxLength={30}
                onChange = {(e: any) => onChange(e)}
                value = {value}
                onCreateOption= {(e) => onCreate(e)}
                loadOptions = { loadOptionsHandler }
                components={{
                    DropdownIndicator: null,
                    ClearIndicator: () => null,
                    Option: (itemProps: any) => {
                        const isDisabled = itemProps.isDisabled;
                        const isNew = itemProps.data.__isNew__;

                        return (
                            <StyledOption {...itemProps.innerProps}>
                                {!isNew ? (
                                    <AppTag
                                        tag={{ ...itemProps.data, title: itemProps.data.title }}
                                        tooltipMessage={
                                            isDisabled
                                                ? "One tag per pipeline is allowed."
                                                : undefined
                                        }
                                    />
                                ) : (
                                    <CreateLabel alignItems gap="5px">
                                        <SvgFragment type="plus" />
                                        <Text $lighter>
                                            Create <Text $bold>{itemProps.data.value}</Text> tag
                                        </Text>
                                    </CreateLabel>
                                )}
                            </StyledOption>
                        );
                    }, 
                    MultiValue: (itemProps: any) => {
                        return (
                            <AppTag 
                                tag={{...itemProps.data, title: itemProps.data.label}} 
                                onDestructiveClickHandler={!itemProps.data.isForbidden ? () => onRemove(itemProps.data) : undefined}
                                />)
                    }, 
                }}
                isOptionDisabled={(option: any) => {
                    // Disable kanban column tags from pipelines that already have another column tag selected
                    if (!option?.forKanban || !(option as any)?.kanban) return false;
                    const pipelineId = String((option as any).kanban);
                    const alreadySelectedForPipeline = value.find(
                        (t: any) =>
                            t?.forKanban &&
                            (t as any)?.kanban &&
                            String((t as any).kanban) === pipelineId &&
                            String(t._id) !== String(option._id)
                    );
                    return !!alreadySelectedForPipeline;
                }}
            />
            {selected && 
            <Column gap="10px">
                <FormControl>
                    <Text $label>{'Color'}</Text>
                    <ColorSelectDropdownFragment 
                        color={selected?.color}
                        onColorChangedHandler={ (color: string) => updateSelected({...selected, color}) }
                        openCustomColorModalHandler={() => setCustomColorModalHandler(selected?.color ?? styles.colors.primary600, (color: string) => updateSelected({...selected, color}))}
                    />
                </FormControl>
                { 
                workspace.isBusiness && <PrivateCheckBoxControl
                    type='tag'
                    value={selected.private ?? true}
                    onChangeHandler={(isPrivate: boolean) => updateSelected({...selected, private: isPrivate})}
                />
                }
            </Column>}
        </Column>
}

const CreateLabel = styled(Row)`
    background: transparent;
    padding: 3px;
    .svg {
        width: 20px;
    }
`

const StyledOption = styled.div`
    padding: 6px 10px;
`

export default ApplyRemoveTagsSelect
